import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { PortalPage } from "@/components/portal/PortalPage";
import { AGENT_NAV } from "@/lib/agent-nav";
import { Seal } from "@/components/brand/Seal";
import { logTeamView } from "@/lib/team.functions";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { inviteRecruit } from "@/lib/strategist.functions";

export const Route = createFileRoute("/agent/team")({
  head: () => ({
    meta: [
      { title: "Your Team — Strategist Portal" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <AgentTeam />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AgentTeam() {
  const log = useServerFn(logTeamView);
  useEffect(() => { void log(); }, [log]);
  const inviteFn = useServerFn(inviteRecruit);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", fullName: "", note: "" });
  const [submitting, setSubmitting] = useState(false);

  async function handleInvite() {
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    setSubmitting(true);
    try {
      await inviteFn({ data: {
        email: form.email.trim(),
        fullName: form.fullName.trim() || null,
        note: form.note.trim() || null,
      }});
      toast.success("Invite sent — they'll appear on your downline once they accept");
      setOpen(false);
      setForm({ email: "", fullName: "", note: "" });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not send invite");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PortalPage
      eyebrow="Hierarchy"
      title="Your Team"
      intro="A full view of your downline, recruit production, and override flow is on the way. For now, your dashboard surfaces your team's headline numbers."
    >
      <div className="flex justify-end">
        <PrimaryButton type="button" onClick={() => setOpen(true)}>+ Invite Recruit</PrimaryButton>
      </div>
      <div className="mx-auto mt-12 flex max-w-xl flex-col items-center text-center">
        <Seal title="Soon" subtitle="In development" size="lg" />
        <p className="mt-8 font-display text-[22px] text-navy-800">
          Your team view is in development.
        </p>
        <p className="mt-3 font-sans text-[14px] leading-relaxed text-charcoal-500">
          We're building a complete genealogy view with three-level rollups, override income detail,
          downline activity, and rank progress. In the meantime, recruit attribution and override
          commissions are tracked automatically — every commission your downline earns credits your
          ledger immediately.
        </p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-navy-800">Invite a Recruit</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-charcoal-500">
            We'll email them an invite to apply as an Advisor. When they accept, they'll be attributed to your downline automatically — overrides on their production credit you.
          </p>
          <div className="mt-4 space-y-3">
            <div>
              <Label htmlFor="rec-name">Full name (optional)</Label>
              <Input id="rec-name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} maxLength={200} />
            </div>
            <div>
              <Label htmlFor="rec-email">Email</Label>
              <Input id="rec-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={320} />
            </div>
            <div>
              <Label htmlFor="rec-note">Personal note (optional)</Label>
              <Textarea id="rec-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} maxLength={500} placeholder="How you know them, why you're inviting them…" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <PrimaryButton type="button" onClick={handleInvite} disabled={submitting}>{submitting ? "Sending…" : "Send Invite"}</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalPage>
  );
}